import { useQuery } from "@tanstack/react-query";
import {
  serializeRange,
  toPresetToken,
  type DateRange,
} from "../filters/dateRange";
import { AlertTriangle, QrCode, Rocket } from "lucide-react";
import { useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

import { fetchSnapFunnel } from "../guerilla/guerillaClient";
import { fetchLaunchpadFunnel } from "../launchpad/launchpadClient";
import { ContactQuickViewModal } from "./ContactQuickViewModal";
import { FunnelColumn } from "./FunnelColumn";
import { fetchFunnelBoard, type FunnelBoardContact } from "./funnelBoard";
import {
  formatInt,
  LAUNCHPAD_SESSION_STAGE_KEY,
  SNAP_QR_STAGE_KEY,
} from "./funnelSummary";

export function FunnelBoardView({
  range,
  leg,
  acquisitionGroup,
}: {
  range: DateRange;
  leg: string;
  acquisitionGroup: string;
}) {
  // Snap/Launchpad funnels are separate services that only take presets.
  const subServiceRange = toPresetToken(range);
  const [selected, setSelected] = useState<FunnelBoardContact | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ["funnel-board", serializeRange(range), leg, acquisitionGroup],
    queryFn: () => fetchFunnelBoard({ range, leg, acquisitionGroup }),
  });

  // Snap-only, count-only pre-WhatsApp column (a scan has no CONTACT# row).
  // Degrades gracefully: on error it simply doesn't render.
  const { data: snapFunnel } = useQuery({
    queryKey: ["snap-funnel", subServiceRange, acquisitionGroup],
    enabled:
      leg === "snap" &&
      (acquisitionGroup === "all" || acquisitionGroup === "organic"),
    queryFn: () => fetchSnapFunnel(subServiceRange),
    retry: false,
  });

  // Launchpad-only, count-only pre-WhatsApp column (a web session has no
  // CONTACT# row). Degrades gracefully: on error it simply doesn't render.
  const { data: launchpadFunnel } = useQuery({
    queryKey: ["launchpad-funnel", subServiceRange, acquisitionGroup],
    enabled:
      leg === "launchpad" &&
      (acquisitionGroup === "all" || acquisitionGroup === "unknown"),
    queryFn: () => fetchLaunchpadFunnel(subServiceRange),
    retry: false,
  });

  if (error) {
    return (
      <Message text={`Could not load the board: ${(error as Error).message}`} />
    );
  }

  if (isPending) {
    return <BoardSkeleton />;
  }

  if (data.cohort_size === 0) {
    return <Message text="No contacts arrived in this cohort yet." />;
  }

  const columns = [...data.columns, data.recurring];

  return (
    <div className="space-y-3">
      {data.incomplete_cohort ? (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertTitle>Recent cohort still converting</AlertTitle>
          <AlertDescription>
            Contacts who just arrived haven&apos;t finished onboarding yet, so
            the later columns will keep filling. Pick an older cohort for a
            settled picture.
          </AlertDescription>
        </Alert>
      ) : null}

      {data.truncated ? (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertTitle>Showing a partial cohort</AlertTitle>
          <AlertDescription>
            This cohort exceeds the board scan limit, so some contacts are
            omitted. Narrow the cohort window to see everyone.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex gap-3 overflow-x-auto pb-4">
        {leg === "snap" && snapFunnel ? (
          <SnapScanColumn
            scanned={
              snapFunnel.stages.find((s) => s.key === SNAP_QR_STAGE_KEY)
                ?.count ?? 0
            }
            neverMessaged={snapFunnel.scanned_not_messaged}
          />
        ) : null}
        {leg === "launchpad" && launchpadFunnel ? (
          <LaunchpadStartColumn
            started={
              launchpadFunnel.stages.find(
                (s) => s.key === LAUNCHPAD_SESSION_STAGE_KEY,
              )?.count ?? 0
            }
            neverMessaged={launchpadFunnel.started_not_messaged}
          />
        ) : null}
        {columns.map((column) => (
          <FunnelColumn
            key={`${column.key}:${serializeRange(range)}:${leg}:${acquisitionGroup}`}
            column={column}
            range={range}
            leg={leg}
            acquisitionGroup={acquisitionGroup}
            onSelect={setSelected}
          />
        ))}
      </div>

      <ContactQuickViewModal
        contact={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

/**
 * Count-only leading column for the Snap board: a QR scan has no CONTACT# row,
 * so there are no cards to page — just the scanned / never-messaged counts,
 * sourced from the bot's token-keyed funnel.
 */
function SnapScanColumn({
  scanned,
  neverMessaged,
}: {
  scanned: number;
  neverMessaged: number;
}) {
  return (
    <div className="flex w-72 shrink-0 flex-col rounded-lg border border-dashed bg-muted/30">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <span className="flex items-center gap-1.5 truncate text-sm font-semibold text-muted-foreground">
          <QrCode className="size-4" />
          QR scanned
        </span>
        <Badge variant="secondary" className="tabular-nums">
          {scanned}
        </Badge>
      </div>
      <div className="flex flex-col items-center justify-center gap-1 p-6 text-center">
        <div className="text-3xl font-semibold tabular-nums">
          {formatInt(scanned)}
        </div>
        <p className="text-xs text-muted-foreground">
          {formatInt(scanned)} scanned · {formatInt(neverMessaged)} never
          messaged
        </p>
        <p className="mt-2 text-[0.7rem] text-muted-foreground">
          Sourced from QR scans — no contact record.
        </p>
      </div>
    </div>
  );
}

/**
 * Count-only leading column for the Launchpad board: a launchpad web session
 * has no CONTACT# row, so there are no cards to page — just the started /
 * never-messaged counts, sourced from the bot's token-keyed funnel.
 */
function LaunchpadStartColumn({
  started,
  neverMessaged,
}: {
  started: number;
  neverMessaged: number;
}) {
  return (
    <div className="flex w-72 shrink-0 flex-col rounded-lg border border-dashed bg-muted/30">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <span className="flex items-center gap-1.5 truncate text-sm font-semibold text-muted-foreground">
          <Rocket className="size-4" />
          Launchpad started
        </span>
        <Badge variant="secondary" className="tabular-nums">
          {started}
        </Badge>
      </div>
      <div className="flex flex-col items-center justify-center gap-1 p-6 text-center">
        <div className="text-3xl font-semibold tabular-nums">
          {formatInt(started)}
        </div>
        <p className="text-xs text-muted-foreground">
          {formatInt(started)} started · {formatInt(neverMessaged)} never
          messaged
        </p>
        <p className="mt-2 text-[0.7rem] text-muted-foreground">
          Sourced from launchpad web sessions — no contact record.
        </p>
      </div>
    </div>
  );
}

function Message({ text }: { text: string }) {
  return (
    <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">
      {text}
    </div>
  );
}

function BoardSkeleton() {
  return (
    <div className="flex gap-3 overflow-x-auto pb-4">
      {Array.from({ length: 6 }).map((_, columnIndex) => (
        <div
          key={columnIndex}
          className="flex w-72 shrink-0 flex-col gap-2 rounded-lg border bg-muted/30 p-2"
        >
          <Skeleton className="h-7 w-full" />
          {Array.from({ length: 4 }).map((_, cardIndex) => (
            <Skeleton key={cardIndex} className="h-16 w-full" />
          ))}
        </div>
      ))}
    </div>
  );
}
